/**
 * @fileoverview Configuração principal de rotas da aplicação
 * @module Routes
 */

/**
 * @typedef {Object} Route
 * @property {string} path - Caminho da rota
 * @property {React.ReactElement} element - Elemento React a ser renderizado
 * @property {Route[]} [children] - Sub-rotas aninhadas
 */

/**
 * @typedef {Object} GuardProps
 * @property {React.ReactNode} children - Componentes filhos a serem protegidos
 */

/**
 * @typedef {Object} RoleBasedGuardProps
 * @property {string} role - Papel necessário para acessar a rota (READ, CREATE, UPDATE, DELETE)
 * @property {string} route - Nome da rota a ser protegida
 * @property {React.ReactNode} children - Componentes filhos a serem protegidos
 */

import lazyWithRetry from "helpers/lazyWithRetry";
import { Suspense } from "react";
import AuthGuard from "./components/AuthGuard";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import GuestGuard from "./components/GuestGuard";
import LoadingScreen from "./components/LoadingScreen";
import MainLayout from "./components/MainLayout";
import RoleBasedGuard from "./components/RoleBasedGuard";
import ReviewLayout from "./pages/dashboard/review/ReviewLayout";
import { Navigate } from "react-router-dom";

const Loadable = (Component) => (props) =>
(
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Authentication pages
const Login = Loadable(
  lazyWithRetry(() => import("./pages/authentication/Login"))
);

// Dashboard pages

const Account = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Account"))
);
const Analytics = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Analytics"))
);

const StoreCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/store/StoreCreate"))
);
const StoreList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/store/StoreList"))
);

const MissionCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/mission/MissionCreate"))
);
const MissionList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/mission/MissionList"))
);
const MissionListOrder = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/mission/MissionListOrder"))
);

const MissionCategoryList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionCategory/MissionCategoryList"))
);
const MissionCategoryCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionCategory/MissionCategoryCreate"))
);
const MissionCategoryEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionCategory/MissionCategoryEdit"))
);

const MissionSubcategoryList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionSubcategory/MissionSubcategoryList"))
);
const MissionSubcategoryCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionSubcategory/MissionSubcategoryCreate"))
);
const MissionSubcategoryEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/missionSubcategory/MissionSubcategoryEdit"))
);

const CouponCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/coupon/CouponCreate"))
);
const CouponList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/coupon/CouponList"))
);
const HotOffersList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/hotOffers/HotOffersList"))
);
const HotOffersCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/hotOffers/HotOffersCreate"))
);

const QuizList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/quiz/QuizList"))
);
const QuizCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/quiz/QuizCreate"))
);

const RewardsCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/rewards/RewardsCreate"))
);
const RewardsList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/rewards/RewardsList"))
);
const RewardsGiftCardSync = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/rewards/RewardsGiftCardSync"))
);
const RewardsGiftCardList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/rewards/RewardsGiftCardList"))
);

const BannerCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/banner/BannerCreate"))
);
const BannerList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/banner/BannerList"))
);
const BannerDetails = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/banner/BannerDetails"))
);
const BannerEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/banner/BannerEdit"))
);

const ModuleOrderList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/module/ModuleOrderList"))
);
const ModuleCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/module/ModuleCreate"))
);
const UserList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/UserList"))
);
const UserDetails = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/UserDetails"))
);

const UserAdminList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/userAdmin/UserAdminList"))
);
const UserAdminCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/userAdmin/UserAdminCreate"))
);
const CarouselList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/carousel/CarouselList"))
);
const CarouselEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/carousel/CarouselEdit"))
);
const CarouselCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/carousel/CarouselCreate"))
);
const CarouselDetails = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/carousel/CarouselDetails"))
);
const CarouselOrderList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/carousel/CarouselOrderList"))
);
const Finance = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Finance"))
);
const Overview = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Overview"))
);

const ModuleList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/module/ModuleList"))
);
const ModuleDetails = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/module/ModuleDetails"))
);

const ExchangeList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/exchange/ExchangeList"))
);

const CategoryList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/category/CategoryList"))
);
const CategoryOrderList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/category/CategoryOrderList"))
);
const CategoryCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/category/CategoryCreate"))
);
const CategoryEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/category/CategoryEdit"))
);

const GroupList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/group/GroupList"))
);
const GroupCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/group/GroupCreate"))
);
const GroupEdit = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/group/GroupEdit"))
);

const BoostedStoreList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/boostedStore/BoostedStoreList"))
);

const PrecouponAWIN = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/precoupon/PrecouponAWIN"))
);
const PrecouponAWINList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/precoupon/PrecouponAWINList"))
);

const Affiliates = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/affiliates/AffiliateList"))
);
const AffiliateCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/affiliates/AffilieateCreate"))
);
const ReportsLeads = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsLeads"))
);
const ReportsLogin = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsLogin"))
);
const ReportsLoginAffiliates = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/reports/ReportsLoginAffiliates")
  )
);
const ReportsRewards = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsRewards"))
);
const ReportsRoulette = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsRoulette"))
);
const ReportsRanking = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsRanking"))
);
const ReportsMission = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsMission"))
);
const ReportsMissionStatus = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsMissionStatus"))
);
const ReportsResearch = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsResearch"))
);
const ReportsReferral = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/reports/ReportsReferral"))
);
const GeneralList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/settings/general/GeneralList"))
);
const Short = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/short/Short"))
);
const LandingPageList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/landingpage/LandingPageList"))
);
const LandingPageCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/landingpage/LandingPageCreate"))
);
const ReviewProductCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/review/ReviewProductCreate"))
);
const ReviewCategoryCreate = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/review/ReviewCategoryCreate"))
);
const MissionApproveList = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/MissionApprove/MissionApproveList")
  )
);
const BigTorcidaList = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/bigTorcida/BigTorcidaList")
  )
);
const BigTorcidaGestaoList = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/bigTorcidaGestao/BigTorcidaGestaoList")
  )
);
const CuponentizadorList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/Cuponentizador/CuponentizadorList"))
);
const CodesList = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/codes/CodesList")
  )
);
const RouletteList = Loadable(
  lazyWithRetry(() =>
    import("./pages/dashboard/Roulette/RouletteList")
  )
);
const ReviewList = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/review/ReviewList"))
);
const ColgateCsvProcess = Loadable(
  lazyWithRetry(() => import("./pages/dashboard/colgate/ColgateCsvProcess"))
);

// Error pages

const AuthorizationRequired = Loadable(
  lazyWithRetry(() => import("./pages/AuthorizationRequired"))
);
const NotFound = Loadable(lazyWithRetry(() => import("./pages/NotFound")));
const ServerError = Loadable(
  lazyWithRetry(() => import("./pages/ServerError"))
);
const PageOffline = Loadable(
  lazyWithRetry(() => import("./pages/PageOffline"))
);

// Other pages

const Home = Loadable(lazyWithRetry(() => import("./pages/Home")));
const routes = [
  {
    path: "singIn",
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: "authentication",
    children: [
      {
        path: "login",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
    ],
  },
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Overview />,
      },
      {
        path: "account",
        element: <Account />,
      },
      {
        path: "analytics",
        element: <Analytics />,
      },
      {
        path: "group",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Grupos">
                <GroupList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Grupos">
                <GroupCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":groupId/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Grupos">
                <GroupEdit />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "category",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Categorias">
                <CategoryList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "order",
            element: (
              <RoleBasedGuard role="UPDATE" route="Categorias">
                <CategoryOrderList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Categorias">
                <CategoryCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":categoryId/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Categorias">
                <CategoryEdit />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "user",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Usuários">
                <UserList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Usuários">
                <UserList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":userId",
            element: (
              <RoleBasedGuard role="READ" route="Usuários">
                <UserDetails />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "useradmin",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Administradores">
                <UserAdminList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Administradores">
                <UserAdminCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "affiliates",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Afiliados">
                <Affiliates />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Categorias">
                <AffiliateCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "landingpage",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Landing Page">
                <LandingPageList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Landing Page">
                <LandingPageList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Landing Page">
                <LandingPageCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "review",
        element: <ReviewLayout />,
        children: [
          {
            path: "",
            element: <Navigate to="produtos" replace />,
          },
          {
            path: ":tab",
            element: <ReviewList />,
          },
          {
            path: ":tab/:orderBy/:order",
            element: <ReviewList />,
          },
          {
            path: "product/new",
            element: (
              <RoleBasedGuard role="CREATE" route="Avaliação">
                <ReviewProductCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: "category/new",
            element: (
              <RoleBasedGuard role="CREATE" route="Avaliação">
                <ReviewCategoryCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsLeads",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de cadastros">
                <ReportsLeads />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsLogin",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de Login">
                <ReportsLogin />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "reportsAffiliatesLogin",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Logins por Afiliado">
                <ReportsLoginAffiliates />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsRewards",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Recompensa">
                <ReportsRewards />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsRoulette",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Análise da Roleta">
                <ReportsRoulette />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsRanking",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de Ranking">
                <ReportsRanking />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsMission",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de Missões">
                <ReportsMission />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsMissionStatus",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Conversão de Missão">
                <ReportsMissionStatus />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsResearch",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de Pesquisas">
                <ReportsResearch />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "ReportsReferral",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Relatório de Indicações">
                <ReportsReferral />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "store",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Lojas">
                <StoreList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Lojas">
                <StoreCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "mission",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Missões">
                <MissionList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Missões">
                <MissionList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Missões">
                <MissionCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: "order",
            element: (
              <RoleBasedGuard role="UPDATE" route="Missões">
                <MissionListOrder />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "mission-category",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Categorias de Missão">
                <MissionCategoryList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Categorias de Missão">
                <MissionCategoryCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":missionCategoryId/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Categorias de Missão">
                <MissionCategoryEdit />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "mission-subcategory",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Categorias de Missão">
                <MissionSubcategoryList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Categorias de Missão">
                <MissionSubcategoryCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Categorias de Missão">
                <MissionSubcategoryEdit />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "banner",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Banner">
                <BannerList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":bannerId",
            element: (
              <RoleBasedGuard role="READ" route="Banner">
                <BannerDetails />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":bannerId/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Banner">
                <BannerEdit />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Banner">
                <BannerCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "carousel",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Carrossel">
                <CarouselList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":carouselId",
            element: (
              <RoleBasedGuard role="READ" route="Carrossel">
                <CarouselDetails />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":carouselId/edit",
            element: (
              <RoleBasedGuard role="UPDATE" route="Carrossel">
                <CarouselEdit />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Carrossel">
                <CarouselCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: "order",
            element: (
              <RoleBasedGuard role="CREATE" route="Carrossel">
                <CarouselOrderList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "modules",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Módulos">
                <ModuleList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":moduleId",
            element: (
              <RoleBasedGuard role="READ" route="Módulos">
                <ModuleDetails />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Módulos">
                <ModuleCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: "order",
            element: <ModuleOrderList />,
          },
        ],
      },
      {
        path: "coupon",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Cupons">
                <CouponList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "precoupon",
            element: (
              <RoleBasedGuard role="CREATE" route="Cupons">
                <PrecouponAWIN />
              </RoleBasedGuard>
            ),
          },
          {
            path: "precoupon/list",
            element: (
              <RoleBasedGuard role="CREATE" route="Cupons">
                <PrecouponAWINList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Cupons">
                <CouponCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "hotOffers",
        children: [
          {
            path: "",
            element: <HotOffersList />,
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Hot Ofertas">
                <HotOffersCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "quiz",
        children: [
          {
            path: "",
            element: <QuizList />,
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Hot Ofertas">
                <QuizCreate />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "misionApprove",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Aprovação de Missão">
                <MissionApproveList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Aprovação de Missão">
                <MissionApproveList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab/:orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Aprovação de Missão">
                <MissionApproveList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "big-torcida",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Big Torcida">
                <BigTorcidaList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Big Torcida">
                <BigTorcidaList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab/:orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Big Torcida">
                <BigTorcidaList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "big-torcida-gestao",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Big Torcida Gestão">
                <BigTorcidaGestaoList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Big Torcida Gestão">
                <BigTorcidaGestaoList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "cuponentizador",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Cuponentizador">
                <CuponentizadorList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Cuponentizador">
                <CuponentizadorList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab/:orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Cuponentizador">
                <CuponentizadorList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "redeemCodes",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Códigos">
                <CodesList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Códigos">
                <CodesList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab/:orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Códigos">
                <CodesList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "roulette",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Roleta">
                <RouletteList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Roleta">
                <RouletteList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab/:orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Roleta">
                <RouletteList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "exchange",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Troca">
                <ExchangeList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":orderBy/:order",
            element: (
              <RoleBasedGuard role="READ" route="Troca">
                <ExchangeList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":notificationOpen",
            element: (
              <RoleBasedGuard role="UPDATE" route="Troca">
                <ExchangeList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "rewards",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Recompensas">
                <RewardsList />
              </RoleBasedGuard>
            ),
          },
          {
            path: ":tab",
            element: (
              <RoleBasedGuard role="READ" route="Recompensas">
                <RewardsList />
              </RoleBasedGuard>
            ),
          },
          {
            path: "new",
            element: (
              <RoleBasedGuard role="CREATE" route="Recompensas">
                <RewardsCreate />
              </RoleBasedGuard>
            ),
          },
          {
            path: "gift_card",
            element: (
              <RoleBasedGuard role="CREATE" route="Recompensas">
                <RewardsGiftCardSync />
              </RoleBasedGuard>
            ),
          },
          {
            path: "gift_card/list",
            element: (
              <RoleBasedGuard role="CREATE" route="Recompensas">
                <RewardsGiftCardList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "boosted-store",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Loja Turbinada">
                <BoostedStoreList />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: "settings",
        children: [
          {
            path: "",
            element: <GeneralList />,
          },
          {
            path: ":configName",
            element: <GeneralList />,
          },
        ],
      },
      {
        path: "finance",
        element: <Finance />,
      },
      {
        path: "short",
        element: (
          <RoleBasedGuard role="READ" route="Encurtador">
            <Short />
          </RoleBasedGuard>
        ),
      },
      {
        path: "colgate",
        children: [
          {
            path: "",
            element: (
              <RoleBasedGuard role="READ" route="Processamento Colgate">
                <ColgateCsvProcess />
              </RoleBasedGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <MainLayout />,
    children: [
      {
        path: "",
        element: <Home />,
      },
      {
        path: "401",
        element: <AuthorizationRequired />,
      },
      {
        path: "404",
        element: <NotFound />,
      },
      {
        path: "500",
        element: <ServerError />,
      },
      {
        path: "503",
        element: <PageOffline />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];
export default routes;
